import React, { useState, useEffect, useCallback } from 'react';
import {
    FileText, Plus, X, Check, Trash2, History, Eye, Send, RotateCcw, Upload,
} from 'lucide-react';
import { bustePagaAPI } from '../../services/personaleApi';
import { messaggioDa } from '../../utils/notifiche';

/**
 * BustePaga — buste paga, CU e contratti delle dipendenti (Alex, 26/10/2026;
 * migration 341).
 *
 *  - LeMieBustePaga: le proprie, pubblicate. La linguetta «Le mie buste paga».
 *  - GestioneBustePaga: carica, anteprima, pubblica, sostituisci, togli,
 *    Cestino. La linguetta «Buste paga», solo per chi ha il PERMESSO
 *    DEDICATO (non per il ruolo).
 *
 * Il confine non lo decide questa schermata: lo decide il server, su ogni
 * rotta (puo_gestire_buste_paga e documento_riservato_accesso nel database).
 * Qui si mostra solo cio' che il server dice di poter fare.
 */

const MESI = ['', 'gennaio', 'febbraio', 'marzo', 'aprile', 'maggio', 'giugno',
              'luglio', 'agosto', 'settembre', 'ottobre', 'novembre', 'dicembre'];
const TIPI = [
    { v: 'busta_paga', t: 'Busta paga' },
    { v: 'cu', t: 'CU / Certificazione Unica' },
    { v: 'contratto', t: 'Contratto' },
];
const GRUPPI = [['busta_paga', 'Buste paga'], ['cu', 'Certificazioni Uniche'], ['contratto', 'Contratti']];

const dataIta = (v) => {
    if (!v) return '—';
    const d = new Date(v);
    return isNaN(d) ? '—' : d.toLocaleDateString('it-IT');
};
const dataOra = (v) => {
    if (!v) return '—';
    const d = new Date(v);
    return isNaN(d) ? '—' : `${d.toLocaleDateString('it-IT')} ${d.toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' })}`;
};

/* Il file si apre in una scheda nuova col gettone di pochi minuti: niente
   link pubblici. Ogni apertura il server la scrive nel registro. */
async function apriInScheda(id) {
    const r = await bustePagaAPI.apri(id);
    window.open(r.data.data.url, '_blank', 'noopener,noreferrer');
}

/* ------------------------------------------------------------------ */
/* Le mie                                                              */
/* ------------------------------------------------------------------ */

export function LeMieBustePaga({ evidenza, onErrore }) {
    const [documenti, setDocumenti] = useState(null);

    const carica = useCallback(async () => {
        try {
            const r = await bustePagaAPI.mie();
            setDocumenti(r.data.data.documenti || []);
        } catch (e) {
            setDocumenti([]);
            onErrore(messaggioDa(e, 'Impossibile caricare le tue buste paga'));
        }
    }, [onErrore]);
    useEffect(() => { carica(); }, [carica]);

    const apri = async (d) => {
        try {
            onErrore(null);
            await apriInScheda(d.id);
            await carica();
        } catch (e) {
            onErrore(messaggioDa(e, 'Impossibile aprire il documento'));
        }
    };

    if (documenti === null) return <p className="adp-vuoto">Caricamento…</p>;
    if (!documenti.length) {
        return (
            <p className="adp-vuoto">
                Non ci sono buste paga per te. Quando ne viene pubblicata una ricevi un avviso e la trovi qui.
            </p>
        );
    }
    return (
        <>
            {GRUPPI.map(([tipo, titolo]) => {
                const righe = documenti.filter((d) => d.tipo === tipo);
                if (!righe.length) return null;
                return (
                    <div key={tipo} className="adp-sezione">
                        <h3>{titolo}</h3>
                        <ul className="adp-lista">
                            {righe.map((d) => (
                                <li key={d.id} className={`adp-voce ${Number(evidenza) === d.id ? 'adp-in-evidenza' : ''}`}>
                                    <FileText size={17} className="adp-icona-file" />
                                    <div className="adp-voce-testo" onClick={() => apri(d)}>
                                        <div className="adp-voce-titolo">
                                            {d.titolo}
                                            {!d.aperta && <span className="adp-pillola">Nuova</span>}
                                        </div>
                                        <div className="adp-voce-sub">Pubblicata il {dataIta(d.pubblicata_il)}</div>
                                    </div>
                                    <button className="adp-icona" title="Apri" onClick={() => apri(d)}>
                                        <Eye size={15} />
                                    </button>
                                </li>
                            ))}
                        </ul>
                    </div>
                );
            })}
            <p className="adp-nota">
                Le vedi solo tu e chi le carica. Ogni apertura resta registrata.
            </p>
        </>
    );
}

/* ------------------------------------------------------------------ */
/* La gestione: solo con il permesso                                   */
/* ------------------------------------------------------------------ */

export function GestioneBustePaga({ onErrore, onAvviso }) {
    const [vista, setVista] = useState('elenco');
    const [documenti, setDocumenti] = useState(null);
    const [cestino, setCestino] = useState(null);
    const [dipendenti, setDipendenti] = useState([]);
    const [modulo, setModulo] = useState(null);
    const [caricato, setCaricato] = useState(null);
    const [accessi, setAccessi] = useState(null);
    const [inCorso, setInCorso] = useState(false);

    const caricaElenco = useCallback(async () => {
        try {
            const r = await bustePagaAPI.elenco();
            setDocumenti(r.data.data.documenti || []);
        } catch (e) {
            setDocumenti([]);
            onErrore(messaggioDa(e, 'Impossibile caricare le buste paga'));
        }
    }, [onErrore]);
    const caricaCestino = useCallback(async () => {
        try {
            const r = await bustePagaAPI.cestino();
            setCestino(r.data.data.documenti || []);
        } catch (e) {
            setCestino([]);
            onErrore(messaggioDa(e, 'Impossibile caricare il Cestino'));
        }
    }, [onErrore]);
    useEffect(() => { caricaElenco(); }, [caricaElenco]);
    useEffect(() => { if (vista === 'cestino') caricaCestino(); }, [vista, caricaCestino]);

    const nuovo = async () => {
        onErrore(null);
        try {
            const r = await bustePagaAPI.dipendenti();
            setDipendenti(r.data.data.dipendenti || []);
        } catch (e) {
            onErrore(messaggioDa(e, 'Impossibile leggere le dipendenti'));
            return;
        }
        const oggi = new Date();
        setCaricato(null);
        setModulo({ tipo: 'busta_paga', dipendente_id: '', anno: oggi.getFullYear(), mese: String(oggi.getMonth() + 1), file: null });
    };

    const invia = async () => {
        try {
            onErrore(null);
            setInCorso(true);
            const fd = new FormData();
            fd.append('file', modulo.file);
            fd.append('tipo', modulo.tipo);
            fd.append('dipendente_id', modulo.dipendente_id);
            fd.append('anno', modulo.anno);
            if (modulo.tipo === 'busta_paga') fd.append('mese', modulo.mese);
            const r = await bustePagaAPI.carica(fd);
            setCaricato({ id: r.data.data.id, sostituira_id: r.data.data.sostituira_id, messaggio: r.data.message });
            await caricaElenco();
        } catch (e) {
            onErrore(messaggioDa(e, 'Errore nel caricamento'));
        } finally {
            setInCorso(false);
        }
    };

    const anteprima = async (id) => {
        try {
            onErrore(null);
            await apriInScheda(id);
        } catch (e) {
            onErrore(messaggioDa(e, 'Impossibile aprire l\'anteprima'));
        }
    };

    /* La sostituzione la chiede il server: un 409 con la domanda, e solo con
       la conferma la vecchia va nel Cestino. */
    const pubblica = async (id) => {
        try {
            onErrore(null);
            setInCorso(true);
            let r;
            try {
                r = await bustePagaAPI.pubblica(id);
            } catch (e) {
                const corpo = e.response?.data;
                if (e.response?.status !== 409 || corpo?.domanda !== 'sostituzione') throw e;
                if (!window.confirm(corpo.message)) return;
                r = await bustePagaAPI.pubblica(id, {
                    conferma_sostituzione: true, sostituisce_id: corpo.data?.sostituisce_id,
                });
            }
            onAvviso(r.data.message);
            setModulo(null);
            setCaricato(null);
            await caricaElenco();
        } catch (e) {
            onErrore(messaggioDa(e, 'Errore nella pubblicazione'));
        } finally {
            setInCorso(false);
        }
    };

    const togli = async (d) => {
        if (!window.confirm(`Togliere «${d.titolo}» di ${d.nominativo}? Va nel Cestino delle buste paga, e da lì si ripristina.`)) return;
        try {
            onErrore(null);
            const r = await bustePagaAPI.togli(d.id);
            onAvviso(r.data.message);
            await caricaElenco();
        } catch (e) {
            onErrore(messaggioDa(e, 'Errore nella rimozione'));
        }
    };

    const ripristina = async (d) => {
        try {
            onErrore(null);
            const r = await bustePagaAPI.ripristina(d.id);
            onAvviso(r.data.message);
            await caricaCestino();
            await caricaElenco();
        } catch (e) {
            onErrore(messaggioDa(e, 'Errore nel ripristino'));
        }
    };

    const vediAccessi = async (d) => {
        try {
            const r = await bustePagaAPI.accessi(d.id);
            setAccessi({ documento: d, righe: r.data.data.accessi || [] });
        } catch (e) {
            onErrore(messaggioDa(e, 'Impossibile leggere il registro'));
        }
    };

    const riga = (d, nelCestino) => (
        <li key={d.id} className="adp-voce">
            <FileText size={17} className="adp-icona-file" />
            <div className="adp-voce-testo">
                <div className="adp-voce-titolo">
                    {d.titolo} — {d.nominativo}
                    {!nelCestino && !d.pubblicata_il && <span className="adp-pillola">Da pubblicare</span>}
                </div>
                <div className="adp-voce-sub">
                    Caricata il {dataOra(d.created_at)}{d.caricata_da ? ` da ${d.caricata_da}` : ''}
                    {d.pubblicata_il && ` · pubblicata il ${dataOra(d.pubblicata_il)}${d.pubblicata_da ? ` da ${d.pubblicata_da}` : ''}`}
                    {nelCestino && ` · nel Cestino dal ${dataOra(d.cancellato_il)}${d.tolta_da ? ` (${d.tolta_da})` : ''}`}
                    {nelCestino && d.sostituita_da && ' · sostituita da una nuova'}
                    {!nelCestino && d.pubblicata_il && (d.aperta_dalla_dipendente ? ' · aperta dalla dipendente' : ' · non ancora aperta dalla dipendente')}
                </div>
            </div>
            <button className="adp-icona" title="Anteprima" onClick={() => anteprima(d.id)}><Eye size={15} /></button>
            <button className="adp-icona" title="Chi l'ha aperta" onClick={() => vediAccessi(d)}><History size={15} /></button>
            {!nelCestino && !d.pubblicata_il && (
                <button className="adp-icona" title="Pubblica" disabled={inCorso} onClick={() => pubblica(d.id)}><Send size={15} /></button>
            )}
            {!nelCestino && (
                <button className="adp-icona" title="Togli (nel Cestino)" onClick={() => togli(d)}><Trash2 size={15} /></button>
            )}
            {nelCestino && (
                <button className="adp-icona" title="Ripristina" onClick={() => ripristina(d)}><RotateCcw size={15} /></button>
            )}
        </li>
    );

    const elenco = vista === 'cestino' ? cestino : documenti;
    return (
        <>
            <div className="adp-azioni-riga">
                <button className="adp-btn adp-btn--principale" onClick={nuovo}>
                    <Plus size={15} /> Carica una busta paga
                </button>
                <button className={`adp-btn ${vista === 'elenco' ? 'adp-filtro-attivo' : ''}`} onClick={() => setVista('elenco')}>
                    Pubblicate e da pubblicare
                </button>
                <button className={`adp-btn ${vista === 'cestino' ? 'adp-filtro-attivo' : ''}`} onClick={() => setVista('cestino')}>
                    <Trash2 size={15} /> Cestino delle buste paga
                </button>
            </div>
            {elenco === null ? <p className="adp-vuoto">Caricamento…</p>
                : !elenco.length ? (
                    <p className="adp-vuoto">
                        {vista === 'cestino' ? 'Il Cestino delle buste paga è vuoto.' : 'Nessuna busta paga caricata.'}
                    </p>
                ) : (
                    <ul className="adp-lista">{elenco.map((d) => riga(d, vista === 'cestino'))}</ul>
                )}
            <p className="adp-nota">
                Buste paga, CU e contratti li vedono solo la dipendente a cui sono intestati e chi ha il
                permesso di gestirli: nessun altro, nemmeno l&apos;amministrazione. Ogni caricamento e ogni
                apertura restano registrati.
            </p>

            {modulo && (
                <div className="adp-sfondo" onClick={() => { setModulo(null); setCaricato(null); }}>
                    <div className="adp-modale" role="dialog" aria-modal="true" onClick={(e) => e.stopPropagation()}>
                        <div className="adp-modale-testa">
                            <h2><Upload size={17} /> Carica una busta paga</h2>
                            <button onClick={() => { setModulo(null); setCaricato(null); }}><X size={17} /></button>
                        </div>
                        {!caricato ? (
                            <>
                                <label className="adp-campo">
                                    <span>Dipendente *</span>
                                    <select value={modulo.dipendente_id}
                                            onChange={(e) => setModulo({ ...modulo, dipendente_id: e.target.value })}>
                                        <option value="">Scegli…</option>
                                        {dipendenti.map((d) => (
                                            <option key={d.id} value={d.id}>{d.nominativo}{d.in_forza ? '' : ' (cessata)'}</option>
                                        ))}
                                    </select>
                                </label>
                                <div className="adp-riga">
                                    <label className="adp-campo">
                                        <span>Tipo *</span>
                                        <select value={modulo.tipo}
                                                onChange={(e) => setModulo({ ...modulo, tipo: e.target.value })}>
                                            {TIPI.map((t) => <option key={t.v} value={t.v}>{t.t}</option>)}
                                        </select>
                                    </label>
                                    {modulo.tipo === 'busta_paga' && (
                                        <label className="adp-campo">
                                            <span>Mese *</span>
                                            <select value={modulo.mese}
                                                    onChange={(e) => setModulo({ ...modulo, mese: e.target.value })}>
                                                {MESI.slice(1).map((m, i) => <option key={i + 1} value={i + 1}>{m}</option>)}
                                            </select>
                                        </label>
                                    )}
                                    <label className="adp-campo">
                                        <span>Anno *</span>
                                        <input type="number" min="2000" max="2100" value={modulo.anno}
                                               onChange={(e) => setModulo({ ...modulo, anno: e.target.value })} />
                                    </label>
                                </div>
                                <label className="adp-campo">
                                    <span>File * (solo PDF)</span>
                                    <input type="file" accept="application/pdf"
                                           onChange={(e) => setModulo({ ...modulo, file: e.target.files?.[0] || null })} />
                                </label>
                                <p className="adp-nota">
                                    Caricata, la busta resta da pubblicare: la dipendente non la vede finché non
                                    controlli l&apos;anteprima e la pubblichi.
                                </p>
                                <div className="adp-modale-fondo">
                                    <button className="adp-btn" onClick={() => setModulo(null)}>Annulla</button>
                                    <button className="adp-btn adp-btn--principale" onClick={invia}
                                            disabled={inCorso || !modulo.file || !modulo.dipendente_id}>
                                        <Check size={15} /> Carica
                                    </button>
                                </div>
                            </>
                        ) : (
                            <>
                                <p className="adp-modale-sub">{caricato.messaggio}</p>
                                {caricato.sostituira_id && (
                                    <p className="adp-nota adp-nota--attenzione">
                                        Per questo periodo ce n&apos;è già una pubblicata: pubblicando, ti verrà chiesto
                                        se sostituirla, e quella di prima andrà nel Cestino delle buste paga.
                                    </p>
                                )}
                                <div className="adp-modale-fondo">
                                    <button className="adp-btn" onClick={() => anteprima(caricato.id)}>
                                        <Eye size={15} /> Anteprima
                                    </button>
                                    <button className="adp-btn" onClick={() => { setModulo(null); setCaricato(null); }}>
                                        Pubblica dopo
                                    </button>
                                    <button className="adp-btn adp-btn--principale" disabled={inCorso}
                                            onClick={() => pubblica(caricato.id)}>
                                        <Send size={15} /> Pubblica
                                    </button>
                                </div>
                            </>
                        )}
                    </div>
                </div>
            )}

            {accessi && (
                <div className="adp-sfondo" onClick={() => setAccessi(null)}>
                    <div className="adp-modale" role="dialog" aria-modal="true" onClick={(e) => e.stopPropagation()}>
                        <div className="adp-modale-testa">
                            <h2><History size={17} /> Chi l&apos;ha aperta</h2>
                            <button onClick={() => setAccessi(null)}><X size={17} /></button>
                        </div>
                        <p className="adp-modale-sub">{accessi.documento.titolo} — {accessi.documento.nominativo}</p>
                        {!accessi.righe.length ? <p className="adp-vuoto">Nessuna apertura.</p> : (
                            <ul className="adp-lista">
                                {accessi.righe.map((a, i) => (
                                    <li key={i} className="adp-voce">
                                        <div className="adp-voce-testo">
                                            <div className="adp-voce-titolo">
                                                {[a.cognome, a.nome].filter(Boolean).join(' ') || 'utente tolto'}
                                                {a.dipendente ? ' (la dipendente)' : ''}
                                            </div>
                                            <div className="adp-voce-sub">{dataOra(a.created_at)}</div>
                                        </div>
                                    </li>
                                ))}
                            </ul>
                        )}
                    </div>
                </div>
            )}
        </>
    );
}
